/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Support sessions: K4D staff opening Housien as a customer from Sauron.
 *
 * Sauron hands over a short-lived Authien token in the URL fragment
 * (`/app/support-access#access_token=...`). The token's `act` claim says who
 * is really acting (`{ sub: <staff>, kind: "support" }`), `scope` says whether
 * changes are allowed, and `support_reason` says why. There is no refresh
 * token: when it expires, the session ends.
 */

import { User } from "oidc-client-ts";

export const SUPPORT_ACTOR_KIND = "support";
export const SUPPORT_WRITE_SCOPE = "support:write";
export const SUPPORT_LANDING_PATH = "/app/workspace";
const STORED_USER_PREFIX = "oidc:user";

export interface SupportClaims {
  /** The customer whose account is open (token `sub`). */
  customer: string;
  /** The K4D staff member acting as the customer (`act.sub`). */
  staff: string;
  reason: string;
  /** Space-separated scopes granted by Authien. */
  scope: string;
  canWrite: boolean;
  /** Expiry, in seconds since the epoch (token `exp`). */
  expiresAt: number;
}

type Claims = Record<string, unknown>;

/** Returns the access token carried in a support-access URL fragment. */
export function parseSupportFragment(hash: string): string | null {
  return new URLSearchParams(hash.replace(/^#/, "")).get("access_token") || null;
}

/** Decodes a JWT payload without verifying it (the API verifies every call). */
export function decodeJwtPayload(token: string): Claims | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
    const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return typeof payload === "object" && payload !== null ? (payload as Claims) : null;
  } catch {
    return null;
  }
}

/** The staff subject of an `act` claim when it is a support actor, else null. */
function supportActor(act: unknown): string | null {
  if (typeof act !== "object" || act === null) return null;
  const { sub, kind } = act as Claims;
  return kind === SUPPORT_ACTOR_KIND && typeof sub === "string" ? sub : null;
}

/** The value as a string, empty for null or undefined. */
const text = (value: unknown): string => (value == null ? "" : String(value));

/** The customer and staff identities of a support payload, or null when it is not one. */
function supportIdentity(payload: Claims | null | undefined): { customer: string; staff: string } | null {
  const staff = supportActor(payload?.act);
  const customer = payload?.sub;
  return staff && typeof customer === "string" ? { customer, staff } : null;
}

/** Reads the support claims out of a token payload or stored profile. */
export function supportClaimsFrom(payload: Claims | null | undefined): SupportClaims | null {
  const identity = supportIdentity(payload);
  if (!identity || !payload) return null;
  const scope = text(payload.scope);
  return {
    ...identity,
    reason: text(payload.support_reason),
    scope,
    canWrite: scope.split(" ").includes(SUPPORT_WRITE_SCOPE),
    expiresAt: Number(payload.exp) || 0,
  };
}

/** Builds the OIDC user the rest of the app reads its session from. */
export function buildSupportUser(token: string, claims: SupportClaims): User {
  const profile = {
    sub: claims.customer,
    email: claims.customer,
    exp: claims.expiresAt,
    scope: claims.scope,
    act: { sub: claims.staff, kind: SUPPORT_ACTOR_KIND },
    support_reason: claims.reason,
  };
  return new User({
    access_token: token,
    token_type: "Bearer",
    scope: claims.scope,
    expires_at: claims.expiresAt,
    profile: profile as unknown as User["profile"],
  });
}

/** The profile stored under the localStorage key, or null when absent or unreadable. */
function storedProfile(key: string): Claims | null {
  try {
    const stored = JSON.parse(localStorage.getItem(key) ?? "null") as { profile?: Claims } | null;
    return stored?.profile ?? null;
  } catch {
    return null;
  }
}

/** Returns the support session stored as the current OIDC user, if any. */
export function getStoredSupportSession(): SupportClaims | null {
  try {
    const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index) ?? "");
    for (const key of keys.filter((name) => name.startsWith(STORED_USER_PREFIX))) {
      const claims = supportClaimsFrom(storedProfile(key));
      if (claims) return claims;
    }
  } catch {
    // Storage blocked (e.g. consent rejected): no session can be stored either.
  }
  return null;
}
